import { useMemo, useState } from "react";
import { useAuth } from "hooks/auth/useAuth";
import { useUnifiedPermissions } from "hooks/auth/useUnifiedPermissions";
import { NavigationPageItem, NavigationItem } from "types/navigation";
import { 
  getSuperUserPages, 
  getFreelancerPages, 
  getStakeholderPages,
  studentPages 
} from "router/pages";
import { AdminPermissionClaims, StakeholderPermissionClaims } from "@freetech/models/user";

/**
 * Scalable page search system that supports multiple search providers.
 * 
 * To add new searchable content:
 * 1. Create a hook that implements SearchProvider interface
 * 2. Return SearchableItem[] from getItems()
 * 3. Pass the provider to usePageSearch({ providers: [yourProvider] })
 * 
 * Example:
 * const clientsProvider = useClientsSearchProvider();
 * const projectsProvider = useProjectsSearchProvider();
 * const { searchResults } = usePageSearch({ 
 *   providers: [clientsProvider, projectsProvider] 
 * });
 */

export interface SearchableItem {
  id: string;
  title: string;
  segment: string;
  path: string;
  icon?: React.ReactNode;
  description?: string;
  category?: string;
  type: 'page' | 'board' | 'card' | 'project' | 'client' | 'other';
}

export interface SearchProvider {
  getItems: () => SearchableItem[];
  isLoading?: boolean;
}

interface UsePageSearchOptions {
  providers?: SearchProvider[];
}

export const usePageSearch = (options: UsePageSearchOptions = {}) => {
  const { role, userInfo } = useAuth();
  const { permissions, isPermissionsLoading } = useUnifiedPermissions();
  const [searchQuery, setSearchQuery] = useState("");
  const { providers = [] } = options;

  const extractSearchablePages = (
    items: NavigationItem[],
    basePath: string = ""
  ): SearchableItem[] => {
    const pages: SearchableItem[] = [];

    for (const item of items) {
      if (item.kind === "page" || ("segment" in item && item.segment)) {
        const pageItem = item as NavigationPageItem;
        if (pageItem.segment && pageItem.title) {
          const fullPath = basePath ? `${basePath}/${pageItem.segment}` : `/${pageItem.segment}`;
          pages.push({
            id: fullPath,
            title: pageItem.title,
            segment: pageItem.segment,
            path: fullPath,
            icon: pageItem.icon,
            type: 'page',
            category: 'Navigation'
          });
        }
      }

      if ("children" in item && item.children) {
        const childPages = extractSearchablePages(
          item.children,
          basePath || (item as any).segment
        );
        pages.push(...childPages);
      }
    }

    return pages;
  };

  const navigationPages = useMemo((): SearchableItem[] => {
    if (!role || isPermissionsLoading || !userInfo) {
      return [];
    }

    let navigationItems: NavigationItem[] = [];

    switch (role) {
      case "admin":
        navigationItems = getSuperUserPages(permissions as AdminPermissionClaims);
        break;
      case "freelancer":
        navigationItems = getFreelancerPages(true, userInfo);
        break;
      case "stakeholder":
        navigationItems = getStakeholderPages(permissions as StakeholderPermissionClaims);
        break;
      case "student":
        navigationItems = studentPages;
        break;
      default:
        return [];
    }

    return extractSearchablePages(navigationItems);
  }, [role, permissions, isPermissionsLoading]);

  const allSearchableItems = useMemo((): SearchableItem[] => {
    const items: SearchableItem[] = [...navigationPages];
    
    providers.forEach(provider => {
      if (!provider.isLoading) {
        items.push(...provider.getItems());
      }
    });

    return items;
  }, [navigationPages, providers]);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();
    return allSearchableItems.filter((item) =>
      item.title.toLowerCase().includes(query) ||
      item.segment.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query)
    );
  }, [searchQuery, allSearchableItems]);

  const clearSearch = () => {
    setSearchQuery("");
  };

  const isLoading = isPermissionsLoading || providers.some(p => p.isLoading);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchablePages: navigationPages, // Keep for backward compatibility
    allSearchableItems,
    clearSearch,
    isLoading,
  };
}; 